import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Betragsfeld, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, toast } from '@elbklar/ui';

import { SelectField, TextField, TextareaField } from '@/components/Felder';
import { transactionsApi } from '@/lib/api';
import { heuteIso } from '@/lib/format';
import { betragFehler, ohneVorzeichen } from '@/lib/betrag';
import { datum, hatFehler, hoechstens, ohne, unlesbareFelder, zumErstenFehler, type Fehler } from '@/lib/pruefen';
import { STEUERSAETZE, steuerHinweis } from '@/lib/steuer';
import type { Category, Transaction } from '@/types';

interface Props {
  offen: boolean;
  onSchliessen: () => void;
  kategorien: Category[];
  onGespeichert: () => void;
  /** Gesetzt = bearbeiten, leer = neu erfassen. */
  buchung?: Transaction | null;
}

const LEER = {
  datum: heuteIso(),
  richtung: 'aus' as 'aus' | 'ein',
  /** Decimal-Text aus dem Betragsfeld, `null` solange keine Zahl dasteht. */
  betrag: '' as string | null,
  empfaenger: '',
  zweck: '',
  kategorie: '',
  steuersatz: '0',
  notiz: '',
};

/**
 * Aus einer bestehenden Buchung wird der Formularzustand.
 *
 * Das Vorzeichen wandert dabei ins Richtungsfeld — der Betrag steht im
 * Formular immer positiv, egal ob neu oder bearbeitet. Sonst muesste man beim
 * Korrigieren an ein Minus denken, das man beim Erfassen nie tippen musste.
 */
function ausBuchung(buchung: Transaction) {
  return {
    datum: buchung.booking_date,
    richtung: (Number(buchung.amount) < 0 ? 'aus' : 'ein') as 'aus' | 'ein',
    betrag: ohneVorzeichen(buchung.amount) as string | null,
    empfaenger: buchung.counterparty ?? '',
    zweck: buchung.purpose ?? '',
    kategorie: buchung.category_id ?? '',
    steuersatz: String(Number(buchung.tax_rate ?? 0)),
    notiz: buchung.notes ?? '',
  };
}

export function TransactionDialog({
  offen,
  onSchliessen,
  kategorien,
  onGespeichert,
  buchung,
}: Props) {
  const [formular, setFormular] = useState(LEER);
  const [fehler, setFehler] = useState<Fehler<'datum' | 'betrag' | 'empfaenger'>>({});

  const bearbeiten = Boolean(buchung);

  useEffect(() => {
    if (!offen) return;
    setFormular(buchung ? ausBuchung(buchung) : LEER);
    setFehler({});
  }, [offen, buchung]);

  const speichern = useMutation({
    mutationFn: () => {
      // Decimal-Text aus dem Betragsfeld, von `absenden` geprüft.
      const betrag = ohneVorzeichen(formular.betrag ?? '');
      const nutzdaten = {
        booking_date: formular.datum,
        // Die Richtung bestimmt das Vorzeichen — der Nutzer tippt nie ein Minus.
        amount: formular.richtung === 'aus' ? `-${betrag}` : betrag,
        counterparty: formular.empfaenger.trim() || null,
        purpose: formular.zweck.trim() || null,
        category_id: formular.kategorie || null,
        tax_rate: formular.steuersatz,
        notes: formular.notiz.trim() || null,
      };

      return buchung
        ? transactionsApi.update(buchung.id, nutzdaten)
        : transactionsApi.create(nutzdaten);
    },
    onSuccess: () => {
      toast.success(bearbeiten ? 'Buchung geändert' : 'Buchung erfasst');
      if (!bearbeiten) setFormular({ ...LEER, datum: formular.datum });
      onGespeichert();
      onSchliessen();
    },
    onError: () => toast.error('Speichern fehlgeschlagen'),
  });

  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const unlesbar = unlesbareFelder(event.currentTarget);
    const neu = {
      datum: datum(formular.datum, { unlesbar: unlesbar.has('datum') }),
      betrag: betragFehler(formular.betrag, { nichtNull: true }),
      empfaenger: hoechstens(formular.empfaenger, 255),
    };
    setFehler(neu);
    if (hatFehler(neu)) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate();
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onSchliessen()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{bearbeiten ? 'Buchung bearbeiten' : 'Buchung erfassen'}</DialogTitle>
        </DialogHeader>
        <form noValidate onSubmit={absenden} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Datum"
              type="date"
              name="datum"
              value={formular.datum}
              onChange={(e) => {
                setFormular({ ...formular, datum: e.target.value });
                setFehler((f) => ohne(f, 'datum'));
              }}
              error={fehler.datum}
              required
            />
            <SelectField
              label="Richtung"
              options={[
                { value: 'aus', label: 'Ausgabe' },
                { value: 'ein', label: 'Einnahme' },
              ]}
              value={formular.richtung}
              onChange={(e) =>
                setFormular({
                  ...formular,
                  richtung: e.target.value as 'aus' | 'ein',
                })
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Betragsfeld
              label="Betrag brutto (€)"
              placeholder="87,45"
              value={formular.betrag ?? ''}
              onChange={(betrag) => {
                setFormular({ ...formular, betrag });
                setFehler((f) => ohne(f, 'betrag'));
              }}
              hinweis={(wert) => steuerHinweis(wert, formular.steuersatz, 'Ohne Vorzeichen, die Richtung steht oben')}
              error={fehler.betrag}
              required
            />
            <SelectField
              label="Umsatzsteuer"
              options={STEUERSAETZE}
              value={formular.steuersatz}
              onChange={(e) =>
                setFormular({ ...formular, steuersatz: e.target.value })
              }
            />
          </div>

          <TextField
            label="Empfänger"
            placeholder="z. B. ARAL AG"
            value={formular.empfaenger}
            onChange={(e) => {
              setFormular({ ...formular, empfaenger: e.target.value });
              setFehler((f) => ohne(f, 'empfaenger'));
            }}
            error={fehler.empfaenger}
          />

          <TextField
            label="Verwendungszweck"
            placeholder="z. B. Diesel"
            value={formular.zweck}
            onChange={(e) => setFormular({ ...formular, zweck: e.target.value })}
          />

          <SelectField
            label="Kategorie"
            options={[{ value: '', label: 'ohne Kategorie' }, ...kategorien.map((k) => ({ value: k.id, label: k.name }))]}
            value={formular.kategorie}
            onChange={(e) => setFormular({ ...formular, kategorie: e.target.value })}
          />

          <TextareaField
            label="Notiz"
            rows={2}
            value={formular.notiz}
            onChange={(e) => setFormular({ ...formular, notiz: e.target.value })}
          />

          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={onSchliessen}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" loading={speichern.isPending}>
              {bearbeiten ? 'Buchung speichern' : 'Buchung erfassen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
